import { createContext, useContext, ReactNode, useCallback, useMemo, useState, useEffect } from 'react';
import { push as navigateTo } from 'connected-react-router';
import { Building, Device, BuildingId, GroupId, DeviceId, EcsExternalId, Group, BuildingProperties } from 'shared/models';
import { Dictionary, values } from 'lodash';
import { callApi } from 'apiClient';
import { useTranslation } from 'react-i18next';
import { toError } from 'shared/util/toError';
import { useDispatch } from 'react-redux';

export type SetupState = {
    regCode: string;
    busy: boolean;
    errorMessage: string | null;
    buildingId: BuildingId | null;
    building: Building | null;
    group: Group | null;
    devices: Dictionary<Device>;
    externalIds: EcsExternalId[];
    currentPanelIndex: number;
    changeRegCode(regCode: string): void;
    changeGroupName(groupId: GroupId, name: string): void;
    changeDeviceName(deviceId: DeviceId, name: string | null): void;
    setBuildingInfo(displayName: string, address: string, properties: BuildingProperties): void;
    reload(): void;
    finish(externalId: string, lastElevator: boolean): void;
};

export const SetupContext = createContext<SetupState>(undefined as any);

export type Props = {
    children?: ReactNode;
};

export const SetupProvider = ({ children }: Props) => {
    const { t } = useTranslation();
    const [ regCode, setRegCode ] = useState('');
    const [ reloadCount, setReloadCount ] = useState(0);
    const [ busy, setBusy ] = useState(false);
    const [ errorMessage, setErrorMessage ] = useState<string | null>(null);
    const [ building, setBuilding ] = useState<Building | null>(null);
    const [ devices, setDevices ] = useState<Dictionary<Device>>({});
    const [ groups, setGroups ] = useState<Dictionary<Group>>({});
    const [ externalIds, setExternalIds ] = useState<EcsExternalId[]>([]);
    const [ currentPanelIndex, setCurrentPanelIndex ] = useState<number>(0);

    const dispatch = useDispatch();
    const buildingId = building && building.id;

    const group = useMemo(() => {
        const featuredDevice = values(devices).find(d => d.properties.regCode === regCode);
        const featuredGroupId = featuredDevice && featuredDevice.groupId;
        return (featuredGroupId && groups && groups[featuredGroupId]) || null;
    }, [ regCode, devices, groups ]);


    //
    // Update the internal representation of the registration code if we were given a full code
    // (i.e. ignore partial codes to avoid making web requests and database hits unnecessarily)
    //
    const changeRegCode = useCallback((value: string) => {
        if (value.length === 14) {
            setRegCode(value);
        }
        else {
            setErrorMessage(null);
        }
    }, [ setRegCode, setErrorMessage ]);

    //
    // Validate the registration code once it is set (or changes)
    // Also reload when the reloadCount counter changes -- we do
    // this when the user clicks the Retry button after a device
    // appeared without a camera... and we're just waiting for
    // the camera to be discovered.
    //
    useEffect(() => {
        if (regCode.length !== 14) {
            return;
        }
        let mounted = true;
        setBusy(true);
        void (async () => {
            try {
                const { building, devices, groups, externalIds } = await callApi('GET', `/api/register/ecs/device/${regCode}`);
                if (mounted) {
                    setErrorMessage(null);
                    setBuilding(building);
                    setDevices(devices);
                    setGroups(groups);
                    setExternalIds(externalIds);
                    setCurrentPanelIndex(building.country ? 2 : 1);
                }
            }
            catch (e) {
                const err = toError(e);
                if (mounted) {
                    const errorMessage = t([ `SETUP.ERROR.${err.message}`, 'SERVER_ERROR' ]);
                    setErrorMessage(errorMessage);
                    setBuilding(null);
                    setDevices({});
                    setExternalIds([]);
                }
            }
            finally {
                if (mounted) {
                    setBusy(false);
                }
            }
        })();

        return () => {
            mounted = false;
        };
    }, [ t, regCode, reloadCount, setBusy, setErrorMessage, setBuilding, setDevices, setExternalIds, setCurrentPanelIndex ]);

    //
    // Trigger a reload of the building, devices, and external IDs
    //
    const reload = useCallback(() => {
        setReloadCount(n => n + 1);
    }, [ setReloadCount ]);

    //
    // Update the name of a group (while maintaining immutability)
    //
    const changeGroupName = useCallback((groupId: GroupId, newName: string) => {
        setGroups(groups => ({
            ...groups,
            [groupId]: {
                ...(groups[groupId] || {}),
                displayName: newName,
            },
        }));
    }, [ setGroups ]);

    //
    // Update the name of a device (while maintaining immutability)
    //
    const changeDeviceName = useCallback((deviceId: DeviceId, newName: string | null) => {
        setDevices(devices => {
            const device = devices[deviceId];
            if (!device) {
                throw new Error(`Device ${deviceId} not found!`);
            }
            return {
                ...devices,
                [deviceId]: {
                    ...device,
                    changed: true, // This flag causes the server to update this device in the database
                    displayName: newName,
                },
            };
        });
    }, [ setDevices ]);

    //
    // Upload the building display name and address to MosaicONE
    //
    const setBuildingInfo = useCallback(async (displayName: string, address: string, properties: BuildingProperties) => {
        if (!buildingId) {
            return;
        }
        setErrorMessage(null);
        setBusy(true);
        try {
            const payload = {
                displayName,
                address,
                properties,
            };
            const building = await callApi('PUT', `/api/buildings/${buildingId}/setup`, payload);
            setBuilding(building);
            setCurrentPanelIndex(2);
        }
        catch (e) {
            const err = toError(e);
            const errorMessage = t([ `SETUP.ERROR.${err.message}`, 'SERVER_ERROR' ]);
            setErrorMessage(errorMessage);
        }
        finally {
            setBusy(false);
        }
    }, [ buildingId, setBusy, setBuilding, setErrorMessage, setCurrentPanelIndex, t ]);
    
    //
    // Upload the elevator and device details to MosaicONE
    //
    const finish = useCallback(async (externalId: string, lastElevator: boolean) => {
        if (!group?.displayName) {
            setErrorMessage(t('SETUP.ELEVATOR_NAME_REQUIRED'));
            return;
        }
        setErrorMessage(null);
        setBusy(true);
        try {
            const payload = {
                devices,
                groupName: group.displayName,
                externalId,
                lastElevator,
            };
            const { elevatorId } = await callApi('PUT', `/api/register/ecs/device/${regCode}`, payload);
            // redirect to the elevator page
            dispatch(navigateTo(`/elevator/${elevatorId}`));
        }
        catch (e) {
            const err = toError(e);
            const errorMessage = t([ `SETUP.ERROR.${err.message}`, 'SERVER_ERROR' ]);
            setErrorMessage(errorMessage);
        }
        finally {
            setBusy(false);
        }
    }, [ regCode, group, devices, setBusy, setErrorMessage, t ]);

    
    const setupState = useMemo<SetupState>(() => Object.freeze({
        regCode,
        reloadCount,
        busy,
        errorMessage,
        buildingId,
        building,
        group,
        devices,
        externalIds,
        currentPanelIndex,
        changeRegCode,
        changeGroupName,
        changeDeviceName,
        setBuildingInfo,
        reload,
        finish,
    }), [ regCode, reloadCount, busy, errorMessage, buildingId, building, group, devices, externalIds, currentPanelIndex, changeRegCode, changeGroupName, changeDeviceName, setBuildingInfo, reload, finish ]);

    return (
        <SetupContext.Provider value={ setupState }>
            { children }
        </SetupContext.Provider>
    );
};

export const useSetup = () => {
    return useContext(SetupContext);
};
